import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, MapPin, Phone, Mail, ArrowRight, Users, Sparkles,
  Home as HomeIcon, Stethoscope, ClipboardCheck, Layers, Building2, PhoneCall,
  Compass,
} from "lucide-react";
import { useSEO, breadcrumbSchema, faqPageSchema, SITE_URL } from "../lib/seo";
import { STATES } from "../data/nationalArchitecture";
import { trackPhoneClick } from "../lib/tracking";

const NATIONAL_SERVICES = [
  { slug: "supported-independent-living", short: "SIL", label: "Supported Independent Living", Icon: HomeIcon, blurb: "24/7 supported homes, including forensic-overlay placements." },
  { slug: "specialist-disability-accommodation", short: "SDA", label: "Specialist Disability Accommodation", Icon: Building2, blurb: "SDA across all four NDIS design categories." },
  { slug: "support-coordination", short: "Coord.", label: "Support Coordination", Icon: ClipboardCheck, blurb: "Complex plan coordination and cross-provider navigation." },
  { slug: "community-nursing-care", short: "Nursing", label: "Community Nursing", Icon: Stethoscope, blurb: "Registered nurses supporting complex health needs at home and in SIL." },
  { slug: "community-participation", short: "Community", label: "Community Participation", Icon: Users, blurb: "1:1 and group community access, skill-building and social connection." },
  { slug: "forensic-disability-services", short: "Forensic", label: "Forensic Disability Services", Icon: ShieldCheck, blurb: "Justice-involved participant support with PBS and clinical governance." },
  { slug: "in-home-support", short: "In-home", label: "In-Home Support", Icon: HomeIcon, blurb: "Daily living, personal care and community access — where you live." },
  { slug: "short-term-accommodation", short: "STA", label: "Short Term Accommodation (STA)", Icon: Sparkles, blurb: "Respite and short-stay supports across our Victorian portfolio." },
  { slug: "medium-term-accommodation", short: "MTA", label: "Medium Term Accommodation (MTA)", Icon: Layers, blurb: "Bridging accommodation for participants transitioning between homes." },
];

const NATIONAL_FAQS = [
  {
    q: "Does Alpha Community Care provide NDIS supports across Australia?",
    a: "Yes. Alpha Community Care is a registered NDIS provider with active physical hubs in Melbourne (VIC) and Perth (WA), and a national intake team that assesses enquiries from every state and territory. Local service availability varies by region and is confirmed case-by-case.",
  },
  {
    q: "Which states does Alpha service?",
    a: "Alpha delivers on-the-ground services in Victoria and Western Australia. For the other states and territories — NSW, QLD, SA, TAS, ACT and NT — we assess referrals through our national intake, typically for specialist SIL, forensic overlay or complex coordination where our interstate capability adds genuine value.",
  },
  {
    q: "Can I refer someone from interstate?",
    a: "Yes. Interstate referrals are welcome — particularly for specialist placements into our Melbourne or Perth SIL homes, forensic-overlay support, or complex plan coordination. Every referral is assessed against genuine fit before we commit.",
  },
  {
    q: "Does Alpha have SIL vacancies nationally?",
    a: "Alpha's SIL portfolio is currently based in Victoria and Western Australia — including Coburg Retreat and Aintree House in Melbourne. Vacancies change weekly and are published on our Our Homes page.",
  },
  {
    q: "How do I check service availability in my area?",
    a: "Call 1300 380 692 or complete a referral form. We acknowledge within 2 business hours and give you an honest answer about whether we can genuinely support the participant.",
  },
  {
    q: "What kinds of referrals is Alpha best suited to?",
    a: "Complex, multi-service and specialist referrals — including SIL with forensic overlay, hospital discharge into SIL, plans requiring community nursing alongside daily living, and interstate SIL placements.",
  },
  {
    q: "Is Alpha's NDIS registration national?",
    a: "Yes. Alpha Community Care is registered under a single national NDIS provider registration, audited against the NDIS Practice Standards. Registration applies wherever we deliver services.",
  },
];

export default function NationalHubPage() {
  useSEO({
    title: "Registered NDIS Provider Australia | Alpha Community Care",
    description: "Alpha Community Care is a registered NDIS provider servicing participants Australia-wide. SIL, SDA, support coordination, community nursing and forensic disability supports — with active hubs in Melbourne and Perth, and national referral capability.",
    keywords: [
      "NDIS provider Australia", "registered NDIS provider Australia", "SIL provider Australia",
      "SDA provider Australia", "supported independent living Australia", "support coordination Australia",
      "NDIS community nursing Australia", "forensic disability services Australia", "complex NDIS provider Australia",
    ],
    canonical: `${SITE_URL}/ndis-provider-australia`,
    jsonLd: [
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "NDIS Provider Australia", path: "/ndis-provider-australia" },
      ]),
      faqPageSchema(NATIONAL_FAQS),
    ],
  });

  return (
    <div data-testid="page-national">
      {/* Hero */}
      <section className="relative overflow-hidden alpha-gradient-hero alpha-noise border-b border-[color:var(--alpha-line)]">
        <div className="alpha-orb alpha-orb-purple w-[360px] h-[360px] -left-24 top-10 opacity-70 hidden md:block" aria-hidden />
        <div className="alpha-orb alpha-orb-green w-[300px] h-[300px] right-[-40px] top-20 opacity-60 hidden md:block" aria-hidden />
        <div className="alpha-container pt-14 pb-14 lg:pt-20 lg:pb-20 relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[color:var(--alpha-line-strong)] bg-white text-xs font-semibold text-[color:var(--alpha-purple-700)]">
            <ShieldCheck className="w-3.5 h-3.5" /> Registered NDIS Provider · Servicing Australia-wide
          </div>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[64px] font-display font-medium tracking-tight leading-[1.05] max-w-4xl">
            A registered NDIS provider — <span className="alpha-serif-italic alpha-gradient-text">delivering across Australia</span>.
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-[color:var(--alpha-mute)]">
            Alpha Community Care is a national NDIS provider with active operational hubs in Melbourne (Victoria) and Perth (Western Australia), and a national intake team supporting participants, families, coordinators and hospitals across every state and territory. Where we can genuinely deliver, we do. Where we can't, we tell you honestly.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/referrals" className="alpha-btn-primary" data-testid="national-cta-refer">
              Make a national referral <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="tel:1300380692" onClick={() => trackPhoneClick("1300 380 692", "national-hero")} className="alpha-btn-green">
              <PhoneCall className="w-4 h-4" /> 1300 380 692
            </a>
            <Link to="/locations" className="alpha-btn-ghost">Explore locations</Link>
          </div>
        </div>
      </section>

      {/* National services */}
      <section className="alpha-section">
        <div className="alpha-container">
          <div className="alpha-overline">National NDIS services</div>
          <h2 className="mt-3 text-3xl sm:text-[40px] font-display font-medium tracking-tight leading-[1.05] max-w-3xl">
            One national capability — with services shaped by real workforce and clinical scope.
          </h2>
          <p className="mt-4 max-w-2xl text-[color:var(--alpha-mute)]">
            These are the core services Alpha Community Care delivers. Availability varies by state — the deepest capability sits in our Victorian and WA hubs, with national intake for specialist and interstate referrals.
          </p>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {NATIONAL_SERVICES.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} className="alpha-card p-6 group hover:-translate-y-1 transition-transform" data-testid={`national-svc-${s.slug}`}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-[color:var(--alpha-purple-50)] text-[color:var(--alpha-purple-700)] flex items-center justify-center group-hover:bg-[color:var(--alpha-purple-700)] group-hover:text-white transition-colors">
                    <s.Icon className="w-5 h-5" />
                  </div>
                  <div className="alpha-overline">{s.short}</div>
                </div>
                <h3 className="mt-4 font-display text-lg font-semibold">{s.label}</h3>
                <p className="mt-2 text-sm text-[color:var(--alpha-mute)]">{s.blurb}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--alpha-purple-700)] group-hover:gap-2 transition-all">Learn more <ArrowRight className="w-3.5 h-3.5" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Where we operate */}
      <section className="alpha-section bg-[color:var(--alpha-bg-soft)]">
        <div className="alpha-container">
          <div className="alpha-overline">Where Alpha operates</div>
          <h2 className="mt-3 text-3xl sm:text-[40px] font-display font-medium tracking-tight leading-[1.05] max-w-3xl">
            Active hubs, national reach — with clear boundaries.
          </h2>
          <p className="mt-4 max-w-2xl text-[color:var(--alpha-mute)]">
            We distinguish between the states where Alpha has an <span className="font-semibold text-[color:var(--alpha-ink)]">operational hub</span> and states where we deliver through our <span className="font-semibold text-[color:var(--alpha-ink)]">national intake model</span>. This isn't hedging — it's how we protect quality.
          </p>

          {/* Active hubs */}
          <div className="mt-10">
            <div className="alpha-overline alpha-overline-green">Active operational hubs</div>
            <div className="mt-4 grid md:grid-cols-2 gap-5">
              {Object.values(STATES).filter((s) => s.capability === "hub").map((s) => (
                <Link key={s.slug} to={`/locations/${s.slug}`} className="alpha-card p-7 group" data-testid={`national-state-${s.slug}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl alpha-gradient text-white flex items-center justify-center font-display text-lg font-semibold">{s.code}</div>
                    <div>
                      <div className="font-display text-2xl font-semibold">{s.name}</div>
                      <div className="text-xs font-semibold text-[color:var(--alpha-green-700)] mt-0.5">Active operational hub</div>
                    </div>
                  </div>
                  <p className="mt-5 text-sm text-[color:var(--alpha-mute)]">{s.tagline}.</p>
                  <div className="mt-4 text-sm font-semibold text-[color:var(--alpha-purple-700)] group-hover:underline">NDIS support in {s.name} <ArrowRight className="w-3.5 h-3.5 inline" /></div>
                </Link>
              ))}
            </div>
          </div>

          {/* Serviced states */}
          <div className="mt-12">
            <div className="alpha-overline">Serviced through national intake</div>
            <p className="mt-2 text-sm text-[color:var(--alpha-mute)] max-w-2xl">Referrals assessed case-by-case — subject to capacity, clinical scope and (where relevant) accommodation. We're honest when a local provider is a better fit.</p>
            <div className="mt-4 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.values(STATES).filter((s) => s.capability !== "hub").map((s) => (
                <Link key={s.slug} to={`/locations/${s.slug}`} className="alpha-card p-6 group" data-testid={`national-state-${s.slug}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-xl bg-[color:var(--alpha-purple-100)] text-[color:var(--alpha-purple-700)] flex items-center justify-center font-display font-semibold">{s.code}</div>
                    <div>
                      <div className="font-display text-lg font-semibold">{s.name}</div>
                      <div className="text-[11px] uppercase tracking-[0.15em] text-[color:var(--alpha-mute)] mt-0.5">National capability</div>
                    </div>
                  </div>
                  <div className="mt-4 text-sm font-semibold text-[color:var(--alpha-purple-700)] group-hover:underline">Details for {s.name} <ArrowRight className="w-3.5 h-3.5 inline" /></div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* National referral model */}
      <section className="alpha-section">
        <div className="alpha-container grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <div className="alpha-overline">National referral model</div>
            <h2 className="mt-3 text-3xl sm:text-[40px] font-display font-medium tracking-tight leading-[1.05]">Refer from anywhere in Australia.</h2>
            <p className="mt-4 text-[color:var(--alpha-mute)]">Our national intake team responds to enquiries from participants, families, support coordinators, hospital discharge planners and other providers across every state — with a bias toward specialist and cross-state placements where Alpha's Melbourne and Perth capability makes a genuine difference.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/referrals/participant" className="alpha-btn-primary">Participant / family <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/referrals/coordinator" className="alpha-btn-green">Coordinator / provider <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { Icon: Compass, t: "Complex & multi-service", d: "SIL + nursing, accommodation + coordination, forensic overlay with clinical oversight." },
              { Icon: Building2, t: "Hospital & discharge", d: "Bridging accommodation, MTA and SIL admission for participants leaving hospital or custody." },
              { Icon: Layers, t: "Cross-state placements", d: "Interstate SIL, MTA or STA where the placement genuinely helps the participant." },
              { Icon: ClipboardCheck, t: "Coordinator handovers", d: "Clean documentation, timeline, funding fit and risk view — before commitment." },
            ].map((c) => (
              <div key={c.t} className="alpha-card p-6" data-testid={`national-referral-${c.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <c.Icon className="w-6 h-6 text-[color:var(--alpha-purple-700)]" />
                <h3 className="mt-3 font-display text-lg font-semibold">{c.t}</h3>
                <p className="text-sm text-[color:var(--alpha-mute)] mt-1">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capacity honesty band */}
      <section className="alpha-section bg-[color:var(--alpha-bg-soft)]">
        <div className="alpha-container max-w-4xl">
          <div className="alpha-overline">A note on capacity</div>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl font-medium leading-tight">Availability varies. We say so.</h2>
          <p className="mt-4 text-[color:var(--alpha-mute)] leading-relaxed">
            Alpha Community Care's ability to deliver a specific service depends on workforce availability, clinical scope, portfolio accommodation and geographic reach. We won't claim service capability we don't have, and we won't take a referral we can't genuinely support. If we're not the right provider, we'll say so — and where possible, point you toward a provider who is.
          </p>
        </div>
      </section>

      {/* National FAQs */}
      <section className="alpha-section">
        <div className="alpha-container max-w-3xl">
          <div className="alpha-overline">National FAQs</div>
          <h2 className="mt-3 font-display text-3xl font-medium">Common national questions.</h2>
          <div className="mt-6 space-y-3">
            {NATIONAL_FAQS.map((f, i) => (
              <details key={f.q} className="alpha-card p-0 overflow-hidden" data-testid={`national-faq-${i}`}>
                <summary className="list-none p-5 flex items-center justify-between cursor-pointer">
                  <span className="font-display text-base font-semibold pr-4">{f.q}</span>
                  <span className="text-[color:var(--alpha-purple-600)] font-bold">+</span>
                </summary>
                <div className="px-5 pb-5 text-sm text-[color:var(--alpha-mute)] leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="alpha-section">
        <div className="alpha-container">
          <div className="relative overflow-hidden rounded-[28px] alpha-gradient p-10 sm:p-14 text-white">
            <div className="grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <h2 className="font-display text-3xl sm:text-4xl lg:text-[44px] font-medium leading-tight !text-white">
                  Refer to Alpha — from anywhere in Australia.
                </h2>
                <p className="mt-4 text-white/85 max-w-2xl">Call, email, or submit a referral. Our national intake team responds within one business day. Honest answers, no doorway pages.</p>
              </div>
              <div className="md:col-span-4 flex flex-col gap-3">
                <Link to="/referrals" className="bg-white text-[color:var(--alpha-purple-700)] rounded-full px-6 py-4 font-semibold text-center inline-flex items-center justify-center gap-2">Make a referral <ArrowRight className="w-4 h-4" /></Link>
                <a href="mailto:hello@alphacommunitycare.com.au" className="border border-white/60 rounded-full px-6 py-4 font-semibold text-center hover:bg-white/10 transition inline-flex items-center justify-center gap-2"><Mail className="w-4 h-4" /> hello@alphacommunitycare.com.au</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
